import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { palette } from '../../constants/styles';

const Colors = {
  default: css`
    color: ${palette.white};
    background-color: ${palette.secondaryColor};
  `,
  alternative: css`
    color: ${palette.white};
    background-color: ${palette.terciaryColor};
  `,
  finalCTA: css`
    color: ${palette.white};
    background-color: #1ee7a9;
  `,
  disabled: css`
    color: ${palette.darkerGray};
    background-color: ${palette.basicGray};
  `,
  gray: css`
    color: white;
    background-color: ${palette.basicGray};
  `,
  darkerGray: css`
    color: white;
    background-color: #b4b4b4;
  `,
  primary: css`
    background-color: ${palette.primaryColor};
    color: ${palette.white};
  `,
  success: css`
    background-color: #22e00b;
    color: ${palette.white};
  `,
};

const StyledButton = styled.button.attrs(({ type }) => ({ type }))`
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 8px;
  outline: none;
  box-shadow: 2px 2px 15px -3px rgba(0, 0, 0, 0.25);
  ${({ shadowColorWhite }) =>
    shadowColorWhite &&
    css`
      box-shadow: 2px 2px 15px -3px rgba(255, 255, 255, 0.25);
    `};

  ${({ noShadow }) =>
    noShadow &&
    css`
      box-shadow: none;
    `};
  ${({ color }) => Colors[color]};
  ${({ small }) =>
    small &&
    css`
      height: 35px;
      font-size: 16px;
    `};
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}px`};
  ${({ styles }) => styles};
  ${({ formButton }) =>
    formButton &&
    css`
      cursor: pointer;
      box-shadow: 2px 2px 15px -3px rgba(0, 0, 0, 0.25);
      z-index: 2000001;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 25px;
      max-width: fit-content;
      :hover {
        background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1));
      }
    `};
  ${({ full }) =>
    full &&
    css`
      font-weight: 600;
      background-color: #3dbc94;
      cursor: pointer;
      padding-top: 20px !important;
      padding-bottom: 20px !important;
      padding-left: 40px !important;
      padding-right: 40px !important;
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 8px;
      box-shadow: inset 0 1px 1px 0 rgba(255, 255, 255, 0.22);
      width: 100%;
      height: fit-content;
      max-width: none;
      :hover {
        background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1));
      }
    `};

  ${({ green }) =>
    green &&
    css`
      cursor: pointer;
      box-shadow: 2px 2px 15px -3px rgba(0, 0, 0, 0.25);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 25px;
      max-width: fit-content;
      :hover {
        background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1));
      }
    `};
`;

const Button = ({
  children,
  color,
  maxWidth,
  small,
  styles,
  onClick,
  shadowColorWhite,
  noShadow,
  type,
  className,
  disabled,
  formButton,
  id,
  ...rest
}) => (
  <StyledButton
    id={id}
    color={color}
    maxWidth={maxWidth}
    small={small}
    styles={styles}
    onClick={onClick}
    shadowColorWhite={shadowColorWhite}
    noShadow={noShadow}
    type={type}
    className={className}
    disabled={disabled}
    formButton={formButton}
    {...rest}
  >
    {children}
  </StyledButton>
);

Button.propTypes = {
  /* Children elements */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.any]),
  shadowColorWhite: PropTypes.bool,
  noShadow: PropTypes.bool,
  formButton: PropTypes.bool,
  /* Background-color */
  color: PropTypes.string,
  type: PropTypes.string,
  /* Max width of the button */
  maxWidth: PropTypes.number,
  small: PropTypes.bool,
  disabled: PropTypes.bool,
  styles: PropTypes.array,
  className: PropTypes.string,
  id: PropTypes.string,
  /* Click callback */
  onClick: PropTypes.func,
};

Button.defaultProps = {
  shadowColorWhite: false,
  noShadow: false,
  formButton: false,
  children: '',
  color: 'default',
  type: 'button',
  maxWidth: 315,
  small: false,
  disabled: false,
  styles: [],
  className: '',
  id: null,
  onClick: () => {},
};

export default Button;
